import crashIcon from "../../../assets/images/car-crash.png";
import privateTow from "../../../assets/images/no-parking.png";
import policeIcon from "../../../assets/images/tow-truck.png";

export interface InformationBox {
    selectionType: string;
    title: string;
    icon: string;
}

export const informationBoxes: InformationBox[] = [
    {
        selectionType: "crash",
        title: "My car was in a crash",
        icon: crashIcon,
    },
    {
        selectionType: "police",
        title: "My car was impounded by police",
        icon: policeIcon,
    },
    {
        selectionType: "private",
        title: "My car was on private property",
        icon: privateTow,
    },
];

export interface ParagraphItem {
    text: string;
    bold?: boolean;
}

export interface BodySection {
    heading: string;
    paragraphs: (string | ParagraphItem)[];
}

export interface StateContent {
    crash: BodySection[];
    police: BodySection[];
    private: BodySection[];
}

export const stateContents: Record<string, StateContent> = {
    UT: {
        crash: [
            {
                heading: "If you have insurance...",
                paragraphs: [
                    "1. Call your insurance company and let them know your vehicle has been involved in an accident",
                    "2. Tell them where your vehicle is being stored",
                    "3. Verify with your insurance company that you will not be liable for the tow and storage fees",
                ],
            },
            {
                heading: "If you have no insurance or have 'liability only' insurance...",
                paragraphs: [
                    "1. Call the tow yard to make arrangements to have your vehicle removed from the impound yard",
                    "If you do not have collision coverage, some policies include towing coverage. Check your insurance policy or ask your insurance agent to verify your coverage.",
                ],
            },
        ],
        police: [
            {
                heading: "To release your vehicle from impound:",
                paragraphs: [
                    "1. Call the tow company to make an appointment and verify any payment amount",
                    "2. Go to the DMV, pay any fees due and obtain a state tax impound release form",
                    "3. Take the letter of impound release and proof of identity to the impound yard",
                    "4. Pay the towing and storage fees",
                    "If you have any questions regarding your impounded vehicle, please contact the impound yard where your vehicle is being located. If you have questions regarding the impound process, please contact the Utah State Tax Commission at 801-297-7780.",
                    "If you fail to claim your vehicle within 30 days of impound your vehicle may be sold at public auction. Utah Code 41-1a-1103, Sale Motor Vehicle Act.",
                ],
            },
        ],
        private: [
            {
                heading: "If your vehicle was removed from private property",
                paragraphs: [
                    "1. Call the tow company where your vehicle is being stored",
                    "2. Present proof of identity",
                    "3. Pay the towing and storage fees",
                ],
            },
        ],
    },
    ID: {
        crash: [
            {
                heading: "If you have insurance...",
                paragraphs: [
                    "1. Call your insurance company and let them know your vehicle has been involved in an accident",
                    "2. Tell them where your vehicle is being stored",
                    "3. Verify with your insurance company that you will not be liable for the tow and storage fees",
                    "Collision coverage → Insurance may pay for damage to your car.",
                    "Liability only → They will not pay for your vehicle damage, but you're still legally insured.",
                ],
            },
            {
                heading: "If you have no insurance or have 'liability only' insurance...",
                paragraphs: [
                    "1. You will handle this yourself – no third parties involved. Call the tow yard to make arrangements to have your vehicle removed from the impound yard.",
                    "2. Pay towing and storage fees",
                    "If you do not have collision coverage, some policies include towing coverage. Check your insurance policy or ask your insurance agent to verify your coverage.",
                ],
            },
        ],
        police: [
            {
                heading: "To release your vehicle from impound...",
                paragraphs: [
                    "1. Call the tow company to make an appointment and verify payment amount",
                    "2. Present proof of identity and ownership",
                    "3. Pay the towing and storage fees",
                    "If you have any questions regarding your impounded vehicle, please contact the impound yard where your vehicle is being located. If you have questions regarding the impound process, please contact the Idaho Division of Motor Vehicles at 208-584-4343.",
                    "If you fail to claim your vehicle within 60 days of impound your vehicle may be sold at public auction. Idaho Code 49-1809 (2025), Sale Motor Vehicle Act.",
                ],
            },
        ],
        private: [
            {
                heading: "If your vehicle was removed from private property...",
                paragraphs: [
                    "1. Call the tow company where your vehicle is being stored",
                    "2. Present proof of identity and ownership",
                    "3. Pay the towing and storage fees",
                ],
            },
        ],
    },
    WI: {
        crash: [
            {
                heading: "If you have insurance...",
                paragraphs: [
                    "1. Call your insurance company and let them know your vehicle has been involved in an accident",
                    "2. Tell them where your vehicle is being stored",
                    "3. Verify with your insurance company that you will not be liable for the tow and storage fees",
                    "Collision coverage → Insurance may pay for damage to your car.",
                    "Liability only → They will not pay for your vehicle damage, but you're still legally insured.",
                ],
            },
            {
                heading: "If you have no insurance or have 'liability only' insurance...",
                paragraphs: [
                    "1. You will handle this yourself – no third parties involved. Call the tow yard to make arrangements to have your vehicle removed from the impound yard.",
                    "2. Bring proof of identify and ownership",
                    "3. Pay towing and storage fees",
                    "If you do not have collision coverage, some policies include towing coverage. Check your insurance policy or ask your insurance agent to verify your coverage.",
                ],
            },
        ],
        police: [
            {
                heading: "To release your vehicle from impound...",
                paragraphs: [
                    "1. Call the tow company or impound lot to verify the location of the vehicle, the amount due, accepted payment methods, and pickup hours.",
                    "2. Ask whether the police agency or municipality has placed any hold on the vehicle or requires a hearing, bond, or separate municipal release before pickup.",
                    "3. Present proof of identity and ownership",
                    "4. Ask for your personal belongings if you need them. Under Wisconsin law, personal property in the vehicle must be released without an extra release fee",
                    "5. Pay the towing and storage fees",
                    "If you have any questions regarding your impounded vehicle, please contact the impound yard where your vehicle is being located.",
                    { text: " If you have questions regarding title, abandoned-vehicle processing, or lien-sale procedures, contact WisDOT DMV.", bold: true },
                    "If you fail to claim your vehicle, it may be sold or transferred after required notices are sent and at least 30 days have passed, in accordance with Wisconsin law. Under Wis. Stat. §§ 779.415 and 342.40.",
                ],
            },
        ],
        private: [
            {
                heading: "If your vehicle was removed from private property...",
                paragraphs: [
                    "1. Call the tow company listed on the posted sign or the police agency that received notice of the tow and confirm where your vehicle is stored.",
                    "2. Bring proof of identity and ownership or other authorization accepted by the tow company.",
                    "3. Pay the ordinary and reasonable towing and storage charges to reclaim the vehicle.",
                    "4. Ask for your personal property if needed. Wisconsin law does not allow an extra charge for removal or release of personal property inside the vehicle.",
                ],
            },
        ],
    },
};
